import React, { FC } from "react";

import { Paper, PaperProps, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { OverridableStringUnion } from "@mui/types";

import { IgCustomTheme } from "./theme";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "8px",
  boxShadow: "none",
  padding: "30px 40px 30px 40px",
  border: `1px solid ${(theme as IgCustomTheme).igBorders.core.default}`,
}));

/**
 * @see Paper
 */

export type IgPaperColor = OverridableStringUnion<
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning"
  | "inherit"
  | "accent",
  {}
>;

interface IgPaperProps extends PaperProps {
  isClickable?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  backgroundColor?: IgPaperColor;
  hoverBackgroundColor?: IgPaperColor;
  selectedBackgroundColor?: IgPaperColor;
  color?: string;
  borderColor?: IgPaperColor;
  hoverBorderColor?: IgPaperColor;
  selectedBorderColor?: IgPaperColor;
}

export const IgPaper: FC<IgPaperProps> = (props) => {
  const {
    children,
    isClickable,
    isSelected,
    isDisabled,
    backgroundColor: backgroundColorProp,
    hoverBackgroundColor: hoverBackgroundColorProp,
    selectedBackgroundColor: selectedBackgroundColorProp,
    color,
    borderColor: borderColorProp,
    hoverBorderColor: hoverBorderColorProp,
    selectedBorderColor: selectedBorderColorProp,
    sx,
    ...rest
  } = props;
  const theme = useTheme();
  const igTheme = theme as IgCustomTheme;

  return (
    <StyledPaper
      sx={{
        ...(isClickable && {
          backgroundColor:
            isSelected && !isDisabled
              ? igTheme.igBackgroundColors.interactive.neutral.active
              : isDisabled
                ? backgroundColorProp &&
                  igTheme.igBackgroundColors.interactive[
                    backgroundColorProp === "primary"
                      ? "core"
                      : backgroundColorProp
                  ]
                  ? igTheme.igBackgroundColors.interactive[
                      backgroundColorProp === "primary"
                        ? "core"
                        : backgroundColorProp
                    ].disabled
                  : igTheme.igBackgroundColors.interactive.neutral.disabled
                : backgroundColorProp &&
                    igTheme.igBackgroundColors.interactive[
                      backgroundColorProp === "primary"
                        ? "core"
                        : backgroundColorProp
                    ]
                  ? igTheme.igBackgroundColors.interactive[
                      backgroundColorProp === "primary"
                        ? "core"
                        : backgroundColorProp
                    ].default
                  : igTheme.igBackgroundColors.baseLightMid,
          "&:hover": {
            backgroundColor: isDisabled
              ? igTheme.igBackgroundColors.interactive.neutral.disabled
              : igTheme.igBackgroundColors.baseLightHigh,
            cursor: !isDisabled ? "pointer" : "default",
            border: `1px solid ${
              isDisabled
                ? hoverBorderColorProp &&
                  igTheme.igBorders[
                    hoverBorderColorProp === "primary"
                      ? "core"
                      : hoverBorderColorProp
                  ]
                  ? igTheme.igBorders[
                      hoverBorderColorProp === "primary"
                        ? "core"
                        : hoverBorderColorProp
                    ].disabled
                  : igTheme.igBorders.core.disabled
                : isClickable && isSelected
                  ? selectedBorderColorProp &&
                    igTheme.igBorders[
                      selectedBorderColorProp === "primary"
                        ? "core"
                        : selectedBorderColorProp
                    ]
                    ? igTheme.igBorders[
                        selectedBorderColorProp === "primary"
                          ? "core"
                          : selectedBorderColorProp
                      ].active
                    : igTheme.igBorders.core.active
                  : hoverBorderColorProp &&
                      igTheme.igBorders[
                        hoverBorderColorProp === "primary"
                          ? "core"
                          : hoverBorderColorProp
                      ]
                    ? igTheme.igBorders[
                        hoverBorderColorProp === "primary"
                          ? "core"
                          : hoverBorderColorProp
                      ].hover
                    : igTheme.igBorders.core.hover
            }`,
          },
        }),
        border: `${isSelected ? "2" : "1"}px solid ${
          isDisabled
            ? borderColorProp &&
              igTheme.igBorders[
                borderColorProp === "primary" ? "core" : borderColorProp
              ]
              ? igTheme.igBorders[
                  borderColorProp === "primary" ? "core" : borderColorProp
                ].disabled
              : igTheme.igBorders.core.disabled
            : isClickable && isSelected
              ? selectedBorderColorProp &&
                igTheme.igBorders[
                  selectedBorderColorProp === "primary"
                    ? "core"
                    : selectedBorderColorProp
                ]
                ? igTheme.igBorders[
                    selectedBorderColorProp === "primary"
                      ? "core"
                      : selectedBorderColorProp
                  ].active
                : igTheme.igBorders.core.active
              : borderColorProp &&
                  igTheme.igBorders[
                    borderColorProp === "primary" ? "core" : borderColorProp
                  ]
                ? igTheme.igBorders[
                    borderColorProp === "primary" ? "core" : borderColorProp
                  ].default
                : igTheme.igBorders.core.default
        }`,
        ...sx,
      }}
      variant={"outlined"}
      elevation={0}
      {...rest}
    >
      {children}
    </StyledPaper>
  );
};
